import { IPermission } from "./AppPermissions";
import { IPermissions } from "./DataModels";

export class Helper
{
    private readonly appliesTo = "APPLIESTO";

    public toFlatPermissions(available: IPermissions, selected: IPermissions): IPermission[]
    {
        const permissions = this.margeAvailableAndSelectedPermissions(available, selected);
        if (!this.arePermissionsValid(permissions))
        {
            throw new Error('Invalid permissions');
        }
        return permissions;
    }

    public toComplexPermissions(permissions: IPermission[]): IPermissions
    {
        const complexPermissions = Object.fromEntries(permissions
            .filter(i => !!i.area && !!i.category && !!i.permission)
            .groupBy(i => i.area)
            .map(area => [
                area.key,
                Object.fromEntries(area.value
                    .groupBy(i => i.category)
                    .map(category => [
                        category.key,
                        category.value.map(i => i.permission),
                    ])
                )
            ]));

        const appliesTo = Object.fromEntries(permissions
            .filter(i => !!i.area && !!i.category && !!i.permission && i.nodeIds.length > 0)
            .map(i => ([[this.fullyQualifiedName(i)], i.nodeIds])));

        const object = {
            ...complexPermissions,
            [this.appliesTo]: appliesTo,
        };
        return object;
    }

    private margeAvailableAndSelectedPermissions(available: IPermissions, selected: IPermissions): IPermission[]
    {
        const availablePermissions = this.fromPermissionsObjectToPermissionArray(available);
        const selectedPermissions = this.fromPermissionsObjectToPermissionArray(selected);
        const permissionNames = [...availablePermissions, ...selectedPermissions].map(i => this.fullyQualifiedName(i)).distinct();
        const mergedPermissions: IPermission[] = [];

        for (const permissionName of permissionNames)
        {
            const available = availablePermissions.find(i => this.fullyQualifiedName(i) == permissionName);
            const selected = selectedPermissions.find(i => this.fullyQualifiedName(i) == permissionName);
            const nameParts = permissionName.split('.');

            mergedPermissions.push({
                area: nameParts[0],
                category: nameParts[1] ?? '',
                permission: nameParts[2] ?? '',
                isAvailable: !!available,
                isSelected: !!selected,
                isExpanded: false,
                nodeIds: selected?.nodeIds ?? [],
            } as IPermission);
        }

        return mergedPermissions;
    }

    private fromPermissionsObjectToPermissionArray(permissions: IPermissions): IPermission[]
    {
        let permissionsArray = Object.entries(permissions)
            .filter(i => i[0] != this.appliesTo)
            .flatMap(area => [
                this.createPermission(area[0] ?? ''),
                ...Object.entries(area[1]).flatMap(category => [
                    this.createPermission(area[0] ?? '', category[0] ?? ''),
                    ...Array.from(category[1]).map(permissionName => this.createPermission(area[0] ?? '', category[0] ?? '', permissionName ?? ''))
                ])
            ]);

        permissionsArray = permissionsArray.filter(i =>
            !!i.area && i.area != "undefined" &&
            i.category != null && i.category != "undefined" &&
            i.permission != null && i.permission != "undefined");

        const appliesTo = permissions[this.appliesTo];
        for (const permissionName in appliesTo)
        {
            const permission = permissionsArray.find(i => this.fullyQualifiedName(i) == permissionName);
            if (!permission)
            {
                continue;
            }
            permission.nodeIds = appliesTo[permissionName];
        }

        return permissionsArray;
    }

    private arePermissionsValid(permissions: IPermission[]): boolean
    {
        const areTypesValid = permissions.every(i => (typeof i.area == "string" && typeof i.category == "string" && typeof i.permission == "string") && Array.isArray(i.nodeIds));
        if (!areTypesValid)
        {
            return false;
        }
        const areUnique = permissions.groupBy(i => this.fullyQualifiedName(i)).every(i => i.value.length == 1);
        if (!areUnique)
        {
            return false;
        }
        return true;
    }

    public createPermission(area: string, category?: string, permission?: string): IPermission
    {
        return ({
            area: area,
            category: category ?? '',
            permission: permission ?? '',
            isAvailable: false,
            isSelected: false,
            isExpanded: false,
            nodeIds: [],
        });
    }

    public fullyQualifiedName(permission: IPermission): string
    {
        return [permission.area, permission.category, permission.permission].filter(i => !!i).join('.');
    }

    public isLeaf(fullyQualifiedName: string): boolean
    {
        return (fullyQualifiedName.split('.').length >= 3);
    }
}
